<template>
  <UikitLoaderBlock v-if="!filtersData && !error" />

  <UikitErrorBlock
    v-else-if="error"
    not-found
    :error="error"
  />

  <section
    v-else-if="filtersData"
    ref="$block"
    class="osk-products-filters-block"
  >
    <div class="osk-wrapper">
      <div
        v-if="title"
        class="osk-products-filters-block__title"
      >
        {{ title }}
      </div>

      <UikitBreadcrumbs
        v-if="breadcrumbs?.length && breadcrumbs.length > 0"
        class="osk-products-filters-block__breadcrumbs"
        :items="breadcrumbs"
      />

      <h1
        v-if="seoData?.headerContent"
        class="osk-products-filters-block__h1"
      >
        {{ seoData.headerContent }}
      </h1>

      <p
        v-if="!isMounted && seoData?.textContent"
        class="osk-products-filters-block__p osk-products-filters-block__p_ssr"
      >
        {{ seoData.textContent }}
      </p>

      <text-clamp
        v-else-if="seoData?.textContent"
        class="osk-products-filters-block__p"
        :text="seoData.textContent"
        :max-lines="2"
      >
        <template #after="{ toggle, clamped, expanded }">
          <span
            class="osk-products-filters-block__text-toggle"
            @click="toggle"
          >
            <template v-if="clamped">{{ $t('common.moreDetails') }}</template>

            <template v-else-if="expanded">{{ $t('common.hide') }}</template>
          </span>
        </template>
      </text-clamp>

      <BusinessProductsFilters
        v-model:loading="filtersLoading"
        :product-filters="filtersData"
        :presets="presetsComp"
        :base-category="baseCategoryComp"
        :product-filters-request="fetchOptions"
        :global-loading="(getFiltersDataPending || productsLoading) && !filtersLoading"
        :sorting="sortingModel"
        :home-banner-id="homeBannerId"
        @update-filters="handleFiltersUpdate"
        @update-boolean-filter="handleBooleanFilterUpdate"
      />

      <div
        v-if="homeBannerId"
        :class="{
          'osk-products-filters-block__blocks_loading': isMounted && ((getFiltersDataPending || productsLoading) && !getMoreProductsPending)
        }"
      >
        <template
          v-for="(block, index) in blocks"
          :key="index"
        >
          <template v-if="block.contentType">
            <component
              :is="BLOCK_COMPONENTS[block.contentType]"
              :data="block"
              no-title
            />
          </template>
        </template>
      </div>

      <ul
        v-if="products.length"
        class="osk-products-filters-block__list"
        :class="{'osk-products-filters-block__list_loading': isMounted && (getFiltersDataPending || productsLoading)}"
      >
        <li
          v-for="(item, index) in products"
          :key="item.productId ?? index"
          ref="$itemRefs"
          class="osk-products-filters-block__item"
        >
          <UikitProductCard
            :index="index"
            :product="item"
            chapter="product_collection"
            :chapter-id="data?.id"
            small-image
            :analytics="{ index, searchId: usageSearchId }"
          />
        </li>
      </ul>

      <ClientOnly>
        <div
          v-if="morePagesCount > 1 && isDesktop && withRouting"
          class="osk-pagination"
        >
          <div
            :class="{
              'osk-pagination__arrow': true,
              'osk-pagination__arrow-prev': true,
              'osk-pagination__arrow_disabled': Number(page ?? 1) <= 1,
            }"
            @click="getNextPageData(Number(props.page ?? 1) - 1)"
          >
            <UikitIcon
              name="ArrowRight"
            />

            <span class="osk-pagination__arrow-text">
              {{ $t('common.back') }}
            </span>
          </div>

          <div class="osk-pagination__text">
            {{ page ?? 1 }} {{ $t('common.from2') }} {{ morePagesCount }}
          </div>

          <div
            :class="{
              'osk-pagination__arrow': true,
              'osk-pagination__arrow-next': true,
              'osk-pagination__arrow_disabled': Number(page ?? 1) >= morePagesCount,
            }"
            @click="getNextPageData(Number(props.page ?? 1) + 1)"
          >
            <span class="osk-pagination__arrow-text">
              {{ $t('common.forward') }}
            </span>

            <UikitIcon
              name="ArrowRight"
            />
          </div>
        </div>

        <UikitMorePagesButton
          v-if="!isDesktop || !withRouting"
          class="osk-products-filters-block__more-pages"
          :loading="getFiltersDataPending || productsLoading || getMoreProductsPending"
          @get-next-page="getNextPageData"
        />
      </ClientOnly>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { ConcreteComponent } from 'vue';

import TextClamp from 'vue3-text-clamp';

import { categoryRootById } from '~/components/catalog/consts';
import type { TFilters, TFiltersPresets } from '~/types/filters';

import {
  ProductFilterRequest, BooleanFilter, PrimaryContentDTO,
  FilterDTO, ProductFilters, SeoDataDTO,
  ContentType, ProductSorting,
} from '~/restAPI/data-contracts';

import { AnalyticsEvents } from '~/types/analytics';

type TComponents = {
  [key in ContentType]?: ConcreteComponent | string
};

const BLOCK_COMPONENTS: TComponents = {
  BANNER: resolveComponent('HomeBlocksBanner'),
  COLLECTION: resolveComponent('HomeBlocksCollection'),
  H_SELECTION: resolveComponent('HomeBlocksHSelection'),
  CELEBRITY: resolveComponent('HomeBlocksCelebrity'),
  ADDITIONAL_COLLECTION: resolveComponent('HomeBlocksAdditionalCollection'),
  USERS_COLLECTION: resolveComponent('HomeBlocksUsersCollection'),
  BLOG: resolveComponent('HomeBlocksBlog'),
  SELECTION: resolveComponent('HomeBlocksSelection'),
  LIST_ITEMS: resolveComponent('HomeBlocksListItems'),
  SINGLE_BANNER: resolveComponent('HomeBlocksSingleBanner'),
  AD_PRODUCT: resolveComponent('HomeBlocksAdProduct'),
  SOCIAL_COLLECTION: resolveComponent('HomeBlocksSocialCollection'),
};

const props = defineProps<{
  breadcrumbs?: IBreadCrumbsItem[]
  filters?: TFilters
  presets?: TFiltersPresets
  sorting?: string
  baseCategory?: IBreadCrumbsItem
  loading?: boolean
  uniqKey?: string
  withRouting?: boolean
  data?: PrimaryContentDTO
  seoData?: SeoDataDTO
  homeBannerId?: string
  page?: string
  needScrollAnalytics?: boolean
}>();

const route = useRoute();

const blocks = useState<PrimaryContentDTO[] | undefined>(`filter-blocks-${props.homeBannerId}`, () => undefined);

const filtersLoading = ref(false);
const $block = ref();
const sortingModel = ref<ProductSorting['code']>();
const $itemRefs = ref<HTMLElement[]>([]);

const { $addEvent } = useNuxtApp();
const { isDesktop, isMounted } = useUiBreakpoints();
const { usageSearchId } = useHeaderSearch();

if (props.needScrollAnalytics) {
  useItemsScrollEvent($itemRefs, scrollEvent);
}

const {
  getFilters, getProductsByFilters, getProductsForBanner,
} = useCatalog();

const {
  morePagesCount,
  moreCurrentPage,
  isStartFromZero,
} = useMorePagesData();

isStartFromZero.value = !!props.homeBannerId;
moreCurrentPage.value = props.page ? Number(props.page) : moreCurrentPage.value;

if (props.homeBannerId) {
  moreCurrentPage.value = 0;
}

const content = computed(() => props.data?.content as FilterDTO | undefined);
const presetsComp = computed(() => props.presets ?? content.value?.presets);

const title = computed(() => content.value?.title);

const isOnlyRootCategory = computed(() => {
  const category = props.filters?.category as number[];

  return category?.length === 1 && Object.keys(categoryRootById).includes(String(category[0]));
});

const baseCategoryId = computed(() => {
  const { filters, baseCategory } = props;

  if (!baseCategory?.id && isOnlyRootCategory.value) {
    return (filters?.category as number[])[0];
  }

  return baseCategory?.id;
});

const baseCategoryComp = computed<IBreadCrumbsItem | undefined>(() => {
  if (baseCategoryId.value && !props.baseCategory?.id) {
    return {
      ...props.baseCategory,
      id: baseCategoryId.value,
    } as IBreadCrumbsItem;
  }

  return props.baseCategory;
});

const filtersComp = computed(() => {
  const result = {
    sorting: props.sorting,
    filters: { ...props.filters },
    presets: { ...presetsComp.value },
  };

  if (!isOnlyRootCategory.value) {
    return result;
  }

  delete result.filters.category;

  return result;
});

const fetchOptions = ref<ProductFilterRequest>(genFetchOptions());

const {
  error,
  data: filtersData,
  pending: getFiltersDataPending,
  refresh: getFiltersData,
} = useLazyAsyncData(`products-filters-block-getData-${props.uniqKey ?? ''}`, async () => {
  if (props.loading) return null;

  const res = await getFilters(fetchOptions.value);

  return res;
});

const {
  data: productsData,
  pending: getProductsPending,
  refresh: getProducts,
} = useLazyAsyncData(async () => {
  if (props.loading || !props.withRouting) return null;

  const res = await getProductsByFilters(fetchOptions.value);

  morePagesCount.value = res?.totalPages ?? 0;

  return res;
});

const {
  pending: getMoreProductsPending,
  execute: getMoreProducts,
} = useLazyAsyncData(async () => {
  const res = await getProductsByFilters({
    ...fetchOptions.value,
    page: moreCurrentPage.value,
  });

  morePagesCount.value = res?.totalPages ?? 0;

  if (productsData.value?.items) {
    if (productsData.value.items.length) {
      productsData.value.items.push(...(res?.items ?? []));
    } else {
      productsData.value.items = res?.items;
    }
  }

  return res;
}, { immediate: false });

getMoreProductsPending.value = false;

const {
  pending: getBannerProductsPending,
  refresh: getBannerProducts,
} = useLazyAsyncData(async () => {
  if (props.loading || props.withRouting || !props.homeBannerId) return null;

  if (moreCurrentPage.value + 1 > 1) {
    getMoreProductsPending.value = true;
  }

  const res = await getProductsForBanner({ id: props.homeBannerId }, {
    ...fetchOptions.value,
    page: moreCurrentPage.value + 1,
    pageLength: 24,
  });

  morePagesCount.value = res?.pageCount ?? 0;
  moreCurrentPage.value = res?.pageNumber ?? 0;

  if (res?.pageNumber === 0) {
    blocks.value = res?.items;
  } else if (blocks.value?.length) {
    blocks.value.push(...(res?.items ?? []));
  }

  getMoreProductsPending.value = false;

  return res;
});

const products = computed(() => productsData.value?.items ?? []);
const updatedFilters = computed(() => !props.loading && filtersComp.value);

const productsLoading = computed(() => getProductsPending.value || getBannerProductsPending.value);

function scrollEvent(index: number) {
  const product = products.value[index];

  if (!product) return;

  const options: any = {
    index,
    item: 'product',
    productId: product.productId,
  };

  if (usageSearchId.value) {
    options.page = 'search';
    options.id_item = usageSearchId.value;
  }

  $addEvent(AnalyticsEvents.BlockView, options);
}

async function getNextPageData(page: number) {
  moreCurrentPage.value = page;

  if (props.homeBannerId) {
    getBannerProducts();
  } else if (isDesktop.value) {
    await navigateTo({
      query: {
        ...route.query,
        page: moreCurrentPage.value,
      },
    });
  } else {
    getMoreProducts();
  }
}

function handleBooleanFilterUpdate(filter: BooleanFilter) {
  const filterRequest: ProductFilterRequest = {
    ...fetchOptions.value,
    filters: {
      ...fetchOptions.value?.filters,
      [filter.code]: !filter.value,
    },
  };

  if (!props.withRouting) {
    fetchOptions.value = filterRequest;
    getFiltersData();
  }

  handleFiltersUpdate(filterRequest);
}

const shouldLoadFilters = ref(true);

async function handleFiltersUpdate(filterRequest?: ProductFilterRequest, allFilters?: ProductFilters | null) {
  if (allFilters) {
    shouldLoadFilters.value = false;
    filtersData.value = allFilters;
  } else {
    shouldLoadFilters.value = true;
  }

  if (props.withRouting) {
    const query = mappedFiltersToQuery({
      sorting: filterRequest?.sorting,
      ...filterRequest?.filters,
    });

    moreCurrentPage.value = 1;

    await navigateTo({ query });
  } else {
    if ($block.value) {
      setTimeout(() => {
        window.scrollTo({
          top: $block.value.offsetTop - (isDesktop.value ? 100 : 58),
        });
      }, 0);
    }

    fetchOptions.value = filterRequest ?? {};
    sortingModel.value = filterRequest?.sorting;

    await getProductsRequest();

    filtersLoading.value = false;
  }
}

function genFetchOptions() {
  return {
    ...filtersComp.value,
    baseCategory: baseCategoryId.value,
    page: Number(props.page ?? 1),
    pageLength: 24,
  };
}

async function getProductsRequest() {
  if (props.homeBannerId) {
    moreCurrentPage.value = 0;
    morePagesCount.value = 0;

    await getBannerProducts();
  } else {
    await getProducts();
  }
}

watch(updatedFilters, async (value) => {
  if (value !== false) {
    fetchOptions.value = genFetchOptions();

    if (filtersData.value && !shouldLoadFilters.value) {
      await getProductsRequest();
    } else {
      await Promise.all([ getFiltersData(), getProductsRequest() ]);
    }

    filtersLoading.value = false;
  }
}, { deep: true });

onUnmounted(() => {
  morePagesCount.value = 0;
  moreCurrentPage.value = 0;
});

defineExpose({
  el: $block,
});
</script>

<style scoped lang="scss">
@import "~/assets/scss/settings/index";

.osk-products-filters-block {
  @include media-query(lg-and-up) {
    padding-bottom: 20px;
  }

  &__breadcrumbs {
    display: none;

    @include media-query(lg-and-up) {
      display: flex;
    }
  }

  &__h1 {
    margin-top: 32px;
    font-size: 18px;
    font-weight: 700;
    line-height: normal;
    text-transform: uppercase;
  }

  &__p {
    margin-top: 15px;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    margin-bottom: 16px;

    @include media-query(lg-and-up) {
      margin-bottom: 0;
    }

    &_ssr {
      @include line-clamp;
    }
  }

  &__text-toggle {
    margin-left: 4px;
    font-weight: 600;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
      text-decoration: none;
    }
  }

  &__list {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -6px;
    padding-top: 16px;

    @include media-query(lg-and-up) {
      margin: 0 -12px;
      padding-top: 0;
    }

    &_loading {
      pointer-events: none;
      opacity: .2;
    }
  }

  &__blocks_loading {
    pointer-events: none;
    opacity: .2;
  }

  :deep(.osk-home-blocks-list-items__list) {
    margin: 0 -22px -32px;

    @include media-query(lg-and-up) {
      margin: 0 -28px -62px;
    }
  }

  &__item {
    width: 50%;
    padding: 0 6px;
    margin-bottom: $indent-mlarge;

    @include media-query(sm-and-up) {
      width: 33.333%;
    }

    @include media-query(md-and-up) {
      width: 25%;
    }

    @include media-query(lg-and-up) {
      padding: 0 12px;
      margin-bottom: 62px;
    }
  }

  &__more-pages {
    margin-bottom: 120px;
    margin-top: 10px;

    @include media-query(lg-and-up) {
      margin-top: -20px;
      margin-bottom: 22px;
    }
  }

  &__title {
    font-size: 18px;
    font-weight: 700;
    line-height: normal;
    margin-bottom: 22px;

    @include media-query(lg-and-up) {
      text-transform: uppercase;
      margin-bottom: 42px;
      text-align: center;
    }
  }
}

.osk-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 40px;

  &__arrow {
    font-weight: $font-weight-bold;
    display: flex;
    align-items: center;

    &:hover {
      cursor: pointer;

      .osk-pagination__arrow-text {
        text-decoration: underline;
      }
    }

    &_disabled {
      color: $grey-2;
      pointer-events: none;

      :deep(.osk-icon) {
        color: $grey-2;
      }
    }
  }

  &__arrow-prev {
    :deep(.osk-icon) {
      transform: rotate(180deg);
      margin-right: $indent-medium;
    }
  }

  &__arrow-next {
    :deep(.osk-icon) {
      margin-left: $indent-medium;
    }
  }

  &__text {
    font-size: $font-size-subheading;
    line-height: normal;
    font-weight: $font-weight-bold;
  }
}
</style>
